import { useEffect, useState } from 'react';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  User,
  Phone,
  Mail,
  Calendar,
  MessageSquare,
  Video,
  Search,
  Plus,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Linkedin,
  Twitter,
  Globe,
  UserCheck,
  UserX,
  Filter,
  Wifi,
  WifiOff,
  Pencil,
} from 'lucide-react';
import { AddSourceDialog } from '@/components/AddSourceDialog';
import { toast } from 'sonner';
import {
  sourcesAtom,
  filteredSourcesAtom,
  selectedSourceAtom,
  sourcesLoadingAtom,
  sourcesErrorAtom,
  sourcesStatusFilterAtom,
  sourcesTypeFilterAtom,
  sourcesSearchQueryAtom,
  fetchSourcesActionAtom,
  deleteSourceActionAtom,
  markSourceContactedActionAtom,
  updateSourceConsentActionAtom,
} from '../stores/sourcesStore';
import type { SourceStatus, SourceType } from '@/types/api';
import { TagsList } from '@/components/source/TagsList';

const getStatusColor = (status: SourceStatus) => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'inactive':
      return 'bg-gray-100 text-gray-800';
    case 'do_not_contact':
      return 'bg-red-100 text-red-800';
    case 'pending_verification':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getTypeColor = (type: SourceType) => {
  switch (type) {
    case 'executive':
      return 'secondary';
    case 'founder':
      return 'default';
    case 'investor':
      return 'outline';
    case 'journalist':
      return 'secondary';
    case 'analyst':
      return 'outline';
    case 'spokesperson':
      return 'secondary';
    case 'expert':
      return 'default';
    default:
      return 'outline';
  }
};

const makeInitials = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`;
};

const makeFullName = (firstName: string, lastName: string) => {
  return `${firstName} ${lastName}`;
};

export function SourceProfile() {
  const sources = useAtomValue(filteredSourcesAtom);
  const allSources = useAtomValue(sourcesAtom);
  const [selectedSource, setSelectedSource] = useAtom(selectedSourceAtom);
  const [statusFilter, setStatusFilter] = useAtom(sourcesStatusFilterAtom);
  const [typeFilter, setTypeFilter] = useAtom(sourcesTypeFilterAtom);
  const [searchQuery, setSearchQuery] = useAtom(sourcesSearchQueryAtom);
  const isLoading = useAtomValue(sourcesLoadingAtom);
  const error = useAtomValue(sourcesErrorAtom);

  const fetchSources = useSetAtom(fetchSourcesActionAtom);
  const deleteSource = useSetAtom(deleteSourceActionAtom);
  const markContacted = useSetAtom(markSourceContactedActionAtom);
  const updateConsent = useSetAtom(updateSourceConsentActionAtom);

  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Fetch sources on component mount
  useEffect(() => {
    fetchSources().catch(console.error);
  }, [fetchSources]);

  // Refetch when filter or search changes with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSources().catch(console.error);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [statusFilter, typeFilter, searchQuery, fetchSources]);

  const handleRefresh = () => {
    fetchSources().catch(console.error);
  };

  const handleDeleteSource = async (sourceId: string) => {
    if (!confirm('Are you sure you want to delete this source?')) return;

    try {
      await deleteSource(sourceId);
      toast.success('Source deleted successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete source';
      toast.error(errorMessage);
    }
  };

  const handleMarkContacted = async (sourceId: string) => {
    try {
      const contactDate = new Date().toISOString();
      await markContacted(sourceId, contactDate);
      toast.success('Contact date updated');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update contact date';
      toast.error(errorMessage);
    }
  };

  const handleToggleConsent = async (
    sourceId: string,
    currentConsent: boolean
  ) => {
    try {
      await updateConsent(sourceId, !currentConsent);
      toast.success(
        `Consent ${!currentConsent ? 'granted' : 'revoked'} successfully`
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update consent';
      toast.error(errorMessage);
    }
  };

  const formatLastContact = (
    dateString: string | null,
    displayString?: string
  ) => {
    if (displayString) return displayString;
    if (!dateString) return 'Never';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInDays === 0) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays} days ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
      return `${Math.floor(diffInDays / 30)} months ago`;
    } catch (e) {
      return 'Unknown';
    }
  };

  // const isMockMode = sourcesService.isMockMode();

  return (
    <div className='h-full flex overflow-hidden'>
      {/* Fixed Source List Sidebar */}
      <div className='w-80 border-r border-border bg-background flex flex-col shrink-0'>
        {/* Fixed Sidebar Header */}
        <div className='p-4 border-b border-border shrink-0'>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-lg font-medium'>Sources</h3>
            <div className='flex items-center gap-2'>
              {/* Mock Mode Toggle for Development */}
              <div className='flex items-center gap-1'></div>
              <Button
                variant='outline'
                size='sm'
                onClick={handleRefresh}
                disabled={isLoading}
                className='gap-1'
              >
                <RefreshCw
                  className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`}
                />
              </Button>
              <AddSourceDialog />
            </div>
          </div>

          {/* Connection Status */}
          <div className='flex items-center gap-2 mb-3'>
            {false ? (
              <>
                <WifiOff className='h-3 w-3 text-orange-500' />
                <span className='text-xs text-orange-600'>Mock mode</span>
              </>
            ) : (
              <>
                <Wifi className='h-3 w-3 text-green-500' />
                <span className='text-xs text-green-600'>Live API</span>
              </>
            )}
            {allSources.length > 0 && (
              <span className='text-xs text-muted-foreground ml-auto'>
                {allSources.length} total, {sources.length} shown
              </span>
            )}
          </div>

          {/* Search */}
          <div className='relative mb-3'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input
              placeholder='Search sources...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='pl-10'
            />
          </div>

          {/* Filters */}
          <div className='space-y-2'>
            <Select
              value={statusFilter}
              onValueChange={(value: 'all' | SourceStatus) =>
                setStatusFilter(value)
              }
            >
              <SelectTrigger className='w-full'>
                <Filter className='h-4 w-4 mr-2' />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Status</SelectItem>
                <SelectItem value='active'>Active</SelectItem>
                <SelectItem value='inactive'>Inactive</SelectItem>
                <SelectItem value='do_not_contact'>Do Not Contact</SelectItem>
                <SelectItem value='pending_verification'>Pending</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={typeFilter}
              onValueChange={(value: 'all' | SourceType) =>
                setTypeFilter(value)
              }
            >
              <SelectTrigger className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Types</SelectItem>
                <SelectItem value='executive'>Executive</SelectItem>
                <SelectItem value='founder'>Founder</SelectItem>
                <SelectItem value='investor'>Investor</SelectItem>
                <SelectItem value='journalist'>Journalist</SelectItem>
                <SelectItem value='analyst'>Analyst</SelectItem>
                <SelectItem value='spokesperson'>Spokesperson</SelectItem>
                <SelectItem value='expert'>Expert</SelectItem>
                <SelectItem value='other'>Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Error state */}
          {error && !false && (
            <Alert className='mt-4' variant='destructive'>
              <AlertTriangle className='h-4 w-4' />
              <AlertDescription className='text-sm'>
                {error}
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleRefresh}
                  className='ml-2'
                >
                  Try Again
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Scrollable Sources List */}
        <div className='flex-1 overflow-hidden'>
          <ScrollArea className='h-full'>
            <div className='p-4'>
              {/* Loading state */}
              {isLoading && sources.length === 0 && (
                <div className='space-y-3'>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Card key={i}>
                      <CardContent className='p-4'>
                        <div className='flex items-start gap-3'>
                          <Skeleton className='w-10 h-10 rounded-full' />
                          <div className='flex-1 space-y-2'>
                            <Skeleton className='h-4 w-3/4' />
                            <Skeleton className='h-3 w-1/2' />
                            <Skeleton className='h-3 w-2/3' />
                            <div className='flex gap-2'>
                              <Skeleton className='h-5 w-16' />
                              <Skeleton className='h-5 w-12' />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!isLoading && sources.length === 0 && !error && (
                <div className='text-center py-8'>
                  <div className='text-muted-foreground mb-4 text-sm'>
                    {searchQuery
                      ? `No sources found matching "${searchQuery}"`
                      : 'No sources found.'}
                  </div>
                  <AddSourceDialog
                    trigger={
                      <Button variant='outline' size='sm' className='gap-2'>
                        <Plus className='h-4 w-4' />
                        Add First Source
                      </Button>
                    }
                  />
                </div>
              )}

              {/* Sources list */}
              <div className='space-y-3'>
                {sources.map(source => (
                  <Card
                    key={source.id}
                    className={`cursor-pointer transition-colors hover:bg-accent/50 ${
                      selectedSource?.id === source.id
                        ? 'bg-accent border-accent-foreground/20'
                        : ''
                    }`}
                    onClick={() => setSelectedSource(source)}
                  >
                    <CardContent className='p-4'>
                      <div className='flex items-start gap-3'>
                        <div className='w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm shrink-0'>
                          {makeInitials(source.firstName, source.lastName)}
                        </div>
                        <div className='flex-1 min-w-0'>
                          <h4 className='font-medium truncate text-sm'>
                            {makeFullName(source.firstName, source.lastName)}
                          </h4>
                          <p className='text-xs text-muted-foreground truncate'>
                            {source.title}
                          </p>
                          <p className='text-xs text-muted-foreground truncate'>
                            {source.company}
                          </p>
                          <div className='flex items-center gap-1 mt-2 flex-wrap'>
                            <Badge
                              className={`text-xs ${getStatusColor(source.status)}`}
                            >
                              {source.status.replace('_', ' ')}
                            </Badge>
                            <Badge
                              variant={getTypeColor(source.sourceType) as any}
                              className='text-xs'
                            >
                              {source.sourceType}
                            </Badge>
                          </div>
                          <div className='text-xs text-muted-foreground mt-1'>
                            Last: {formatLastContact(source.lastContacted)}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Main Content Area */}
      <div className='flex-1 flex flex-col overflow-hidden'>
        {selectedSource ? (
          <>
            {/* Fixed Header */}
            <div className='p-4 border-b border-border bg-background shrink-0'>
              <div className='flex items-start gap-6 mb-4'>
                <div className='w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-xl shrink-0'>
                  {makeInitials(
                    selectedSource.firstName,
                    selectedSource.lastName
                  )}
                </div>
                <div className='space-y-2'>
                  <div className='flex items-start justify-between gap-4'>
                    <div className='flex flex-col items-start'>
                      <h2 className='text-xl font-medium'>
                        {makeFullName(
                          selectedSource.firstName,
                          selectedSource.lastName
                        )}
                      </h2>
                      <div className='flex'>
                        <p className='text-muted-foreground text-sm'>
                          {selectedSource.title} {selectedSource.company}
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center gap-2 mt-2 flex-wrap'>
                      <Badge className={getStatusColor(selectedSource.status)}>
                        {selectedSource.status.replace('_', ' ')}
                      </Badge>
                      <Badge
                        variant={getTypeColor(selectedSource.sourceType) as any}
                      >
                        {selectedSource.sourceType}
                      </Badge>
                      {selectedSource.consentGiven ? (
                        <Badge
                          variant='outline'
                          className='text-green-700 bg-green-50'
                        >
                          <CheckCircle className='h-3 w-3 mr-1' />
                          Consent Given
                        </Badge>
                      ) : (
                        <Badge
                          variant='outline'
                          className='text-red-700 bg-red-50'
                        >
                          <XCircle className='h-3 w-3 mr-1' />
                          No Consent
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className='flex gap-2 shrink-0'>
                    <Button
                      size='sm'
                      variant='outline'
                      className='gap-2'
                      onClick={() => setEditDialogOpen(true)}
                    >
                      <Pencil className='h-4 w-4' />
                      Edit
                    </Button>
                    <Button
                      size='sm'
                      variant='outline'
                      className='gap-2'
                      onClick={() => handleMarkContacted(selectedSource.id!)}
                      disabled={isLoading}
                    >
                      <MessageSquare className='h-4 w-4' />
                      Mark Contacted
                    </Button>
                    <Button size='sm' className='gap-2'>
                      <Video className='h-4 w-4' />
                      Schedule Interview
                    </Button>
                  </div>
                </div>
              </div>

              <div className='grid grid-cols-4 gap-4'>
                <Card>
                  <CardContent className='p-3 text-center'>
                    <div className='text-xl font-medium text-primary'>
                      {formatLastContact(selectedSource.lastContacted)}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      Last Contact
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className='flex-1 overflow-hidden'>
              <ScrollArea className='h-full'>
                <div className='p-4'>
                  <Tabs defaultValue='details' className='w-full'>
                    <TabsList className='grid w-full grid-cols-4'>
                      <TabsTrigger value='details'>Contact Details</TabsTrigger>
                      <TabsTrigger value='history'>
                        Interview History
                      </TabsTrigger>
                      <TabsTrigger value='notes'>Notes</TabsTrigger>
                      <TabsTrigger value='actions'>Actions</TabsTrigger>
                    </TabsList>

                    <TabsContent value='details' className='space-y-4 mt-4'>
                      <Card>
                        <CardHeader>
                          <CardTitle className='text-base'>
                            Contact Information
                          </CardTitle>
                        </CardHeader>
                        <CardContent className='space-y-3'>
                          {selectedSource.email && (
                            <div className='flex items-center gap-3'>
                              <Mail className='h-4 w-4 text-muted-foreground' />
                              <span className='text-sm'>
                                {selectedSource.email}
                              </span>
                            </div>
                          )}
                          {selectedSource.phone && (
                            <div className='flex items-center gap-3'>
                              <Phone className='h-4 w-4 text-muted-foreground' />
                              <span className='text-sm'>
                                {selectedSource.phone}
                              </span>
                            </div>
                          )}
                          {selectedSource.preferredContactMethod && (
                            <div className='flex items-center gap-3'>
                              <MessageSquare className='h-4 w-4 text-muted-foreground' />
                              <span className='text-sm'>
                                Preferred:{' '}
                                {selectedSource.preferredContactMethod}
                              </span>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {(selectedSource.website ||
                        selectedSource.linkedinUrl ||
                        selectedSource.twitterHandle) && (
                        <Card>
                          <CardHeader>
                            <CardTitle className='text-base'>
                              Online Presence
                            </CardTitle>
                          </CardHeader>
                          <CardContent className='space-y-3'>
                            {selectedSource.linkedinUrl && (
                              <div className='flex items-center gap-3'>
                                <Linkedin className='h-4 w-4 text-muted-foreground' />
                                <a
                                  href={selectedSource.linkedinUrl}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-sm text-primary hover:underline'
                                >
                                  LinkedIn Profile
                                </a>
                              </div>
                            )}
                            {selectedSource.twitterHandle && (
                              <div className='flex items-center gap-3'>
                                <Twitter className='h-4 w-4 text-muted-foreground' />
                                <span className='text-sm'>
                                  {selectedSource.twitterHandle}
                                </span>
                              </div>
                            )}
                            {selectedSource.website && (
                              <div className='flex items-center gap-3'>
                                <Globe className='h-4 w-4 text-muted-foreground' />
                                <a
                                  href={selectedSource.website}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-sm text-primary hover:underline'
                                >
                                  {selectedSource.website}
                                </a>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {selectedSource.expertiseAreas && (
                        <Card>
                          <CardHeader>
                            <CardTitle className='text-base'>
                              Expertise Areas
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className='flex flex-wrap gap-2'>
                              {selectedSource.expertiseAreas}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {selectedSource.tags && (
                        <Card>
                          <CardHeader>
                            <CardTitle className='text-base'>Tags</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <TagsList tagsList={selectedSource.tags} />
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    <TabsContent value='history' className='space-y-4 mt-4'>
                      <div className='text-center py-12 text-muted-foreground'>
                        <Calendar className='h-12 w-12 mx-auto mb-4 opacity-50' />
                        <p className='text-sm'>
                          Interview history feature coming soon
                        </p>
                      </div>
                    </TabsContent>

                    <TabsContent value='notes' className='mt-4'>
                      <Card>
                        <CardHeader>
                          <CardTitle className='text-base'>Notes</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className='text-sm'>
                            {selectedSource.notes || 'No notes available'}
                          </p>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value='actions' className='space-y-4 mt-4'>
                      <Card>
                        <CardHeader>
                          <CardTitle className='text-base'>
                            Source Actions
                          </CardTitle>
                        </CardHeader>
                        <CardContent className='space-y-4'>
                          <div className='flex items-center justify-between'>
                            <div>
                              <p className='font-medium text-sm'>
                                Consent Status
                              </p>
                              <p className='text-xs text-muted-foreground'>
                                Toggle consent for journalistic contact
                              </p>
                            </div>
                            <Button
                              variant={
                                selectedSource.consentGiven
                                  ? 'outline'
                                  : 'default'
                              }
                              size='sm'
                              onClick={() =>
                                handleToggleConsent(
                                  selectedSource.id!,
                                  selectedSource.consentGiven
                                )
                              }
                              disabled={isLoading}
                              className='gap-2'
                            >
                              {selectedSource.consentGiven ? (
                                <>
                                  <UserX className='h-4 w-4' />
                                  Revoke Consent
                                </>
                              ) : (
                                <>
                                  <UserCheck className='h-4 w-4' />
                                  Grant Consent
                                </>
                              )}
                            </Button>
                          </div>

                          <div className='border-t pt-4'>
                            <div className='flex items-center justify-between'>
                              <div>
                                <p className='font-medium text-destructive text-sm'>
                                  Danger Zone
                                </p>
                                <p className='text-xs text-muted-foreground'>
                                  Permanently delete this source
                                </p>
                              </div>
                              <Button
                                variant='destructive'
                                size='sm'
                                onClick={() =>
                                  handleDeleteSource(selectedSource.id!)
                                }
                                disabled={isLoading}
                              >
                                Delete Source
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </div>
              </ScrollArea>
            </div>
          </>
        ) : (
          <div className='flex-1 flex items-center justify-center bg-muted/20'>
            <div className='text-center'>
              <User className='h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50' />
              <p className='text-muted-foreground'>
                Select a source to view details
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Edit source dialog */}
      {selectedSource && (
        <AddSourceDialog
          source={selectedSource}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        />
      )}
    </div>
  );
}
